import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Tooltip } from './Tooltip';
// Centralisation 2026-05-21 (rétrocheck refonte pipeline) : on importe le
// type + la table des tokens visuels depuis le service plutôt que de garder
// une copie locale qui dérivait à chaque retouche de palette. Le composant
// reste un primitif UI — la dépendance est unidirectionnelle (ce dictionnaire
// ne dépend pas de React).
import { STAGE_VISUAL, type OpportunityStage } from '@/services/opportunities.api';

type ProspectStatus = 'cold' | 'warm' | 'client';
type ProspectPotential = 'low' | 'medium' | 'high' | 'very_high';

const baseBadge =
  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap';

const STATUS_STYLES: Record<ProspectStatus, { classes: string; label: string }> = {
  cold: { classes: 'bg-gray-100 text-gray-700', label: 'Froid' },
  warm: { classes: 'bg-warning-50 text-warning-600', label: 'Chaud' },
  client: { classes: 'bg-success-50 text-success-600', label: 'Client' },
};

export function StatusBadge({ status }: { status: ProspectStatus }) {
  const s = STATUS_STYLES[status];
  return <span className={cn(baseBadge, s.classes)}>{s.label}</span>;
}

const POTENTIAL_STYLES: Record<ProspectPotential, { classes: string; label: string }> = {
  low: { classes: 'text-text-muted', label: 'Faible' },
  medium: { classes: 'text-brand-400', label: 'Moyen' },
  high: { classes: 'text-accent', label: 'Fort' },
  very_high: { classes: 'text-danger font-semibold', label: 'Très fort' },
};

/**
 * Contenu informatif affiché au hover de la pastille « Potentiel ». Calcul
 * 100 % automatique à partir des opportunités du prospect - le tooltip
 * expose la formule pour que le collaborateur comprenne immédiatement
 * pourquoi le prospect est classé comme il l'est.
 */
const POTENTIAL_TOOLTIP = (
  <div className="space-y-1.5">
    <p className="font-semibold">Potentiel commercial (auto)</p>
    <p className="text-white/80">
      Calculé automatiquement à partir des opportunités du prospect : somme du CA pondéré des opps
      ouvertes + commission signée des opps remportées.
    </p>
    <ul className="mt-1 space-y-0.5 text-white/90">
      <li>
        <span className="font-medium">Faible</span> - &lt; 500 €
      </li>
      <li>
        <span className="font-medium">Moyen</span> - 500 à 2 000 €
      </li>
      <li>
        <span className="font-medium">Fort</span> - 2 000 à 5 000 €
      </li>
      <li>
        <span className="font-medium">Très fort</span> - &gt; 5 000 €
      </li>
    </ul>
    <p className="text-[10px] text-white/70">
      Mis à jour à chaque création, modification ou signature d&apos;une opportunité.
    </p>
  </div>
);

export function PotentialBadge({ potential }: { potential: ProspectPotential | null }) {
  if (!potential) {
    return (
      <Tooltip content={POTENTIAL_TOOLTIP}>
        <span className="text-xs text-gray-400">-</span>
      </Tooltip>
    );
  }
  const s = POTENTIAL_STYLES[potential];
  return (
    <Tooltip content={POTENTIAL_TOOLTIP}>
      <span className={cn('cursor-help text-xs', s.classes)}>{s.label}</span>
    </Tooltip>
  );
}

export function PipelineStageBadge({
  stage,
  size = 'sm',
  className,
}: {
  stage: OpportunityStage;
  /** `sm` (défaut) : padding compact pour les listes. `md` : taille standard
   *  - utilisée à côté d'un titre de card. `lg` : version mise en avant
   *  pour du contexte très visible. */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const s = STAGE_VISUAL[stage];
  // Style Stripe : coin arrondi discret (`rounded-md` = 6 px), pas de pilule
  // arrondie totale. Tailles compactes, font-semibold pour une lisibilité
  // immédiate du statut.
  const sizing: Record<'sm' | 'md' | 'lg', string> = {
    sm: 'px-1.5 py-0.5 text-[11px]',
    md: 'px-2 py-0.5 text-xs',
    lg: 'px-2.5 py-1 text-sm',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md whitespace-nowrap font-semibold',
        sizing[size],
        s.badge,
        className,
      )}
    >
      {s.label}
    </span>
  );
}

/**
 * Badge générique pour cas hors du référentiel (ex: tags utilisateur).
 */
export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'purple';

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}) {
  const tones: Record<BadgeTone, string> = {
    neutral: 'bg-gray-100 text-gray-700',
    accent: 'bg-accent-50 text-accent-600',
    success: 'bg-success-50 text-success-600',
    warning: 'bg-warning-50 text-warning-600',
    danger: 'bg-danger-50 text-danger-600',
    // Violet Stripe historique (#635BFF) - réservé V1 aux événements d'appel.
    purple: 'bg-[#EEEDFF] text-[#5851DB]',
  };
  return <span className={cn(baseBadge, tones[tone], className)}>{children}</span>;
}
